import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronRight, RefreshCcw } from "lucide-react";

import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { getNotifications, markNotificationsRead } from "@/lib/api";
import {
  matchesNotificationTab,
  notificationSectionLabel,
  notificationTabs,
  withNotificationUiFields,
} from "@/lib/notifications";
import { toast } from "@/components/ui/sonner";

const SECTION_ORDER = ["today", "yesterday", "this_week", "earlier"];

export default function NotificationsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadNotifications = async ({ showSpinner = true, autoMarkRead = true } = {}) => {
    if (showSpinner) setIsLoading(true);
    try {
      const rows = await getNotifications({ limit: 200 });
      const normalizedRows = withNotificationUiFields(rows || []);
      setNotifications(normalizedRows);

      if (autoMarkRead) {
        const unreadIds = normalizedRows.filter((item) => item.status !== "read").map((item) => item.id);
        if (unreadIds.length > 0) {
          await markNotificationsRead({ ids: unreadIds });
          setNotifications((prev) => prev.map((item) => ({ ...item, status: "read" })));
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      if (showSpinner) setIsLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications({ showSpinner: true, autoMarkRead: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleItems = useMemo(
    () => notifications.filter((item) => matchesNotificationTab(item, activeTab)),
    [activeTab, notifications],
  );

  const groupedBySection = useMemo(() => {
    const sections = {
      today: [],
      yesterday: [],
      this_week: [],
      earlier: [],
    };
    visibleItems.forEach((item) => {
      const key = item.section || "earlier";
      if (!sections[key]) sections[key] = [];
      sections[key].push(item);
    });
    return sections;
  }, [visibleItems]);

  const tabCounts = useMemo(() => {
    const base = { all: notifications.length };
    notificationTabs.forEach((tab) => {
      if (tab.id === "all") return;
      base[tab.id] = notifications.filter((item) => matchesNotificationTab(item, tab.id)).length;
    });
    return base;
  }, [notifications]);

  const onRefresh = async () => {
    setIsRefreshing(true);
    await loadNotifications({ showSpinner: false, autoMarkRead: true });
    setIsRefreshing(false);
  };

  return (
    <AppShell user={user} onLogout={onLogout}>
      <div className="notifications-page" data-testid="notifications-page-root">
        <header className="notifications-page-header" data-testid="notifications-page-header">
          <div>
            <h1 className="notifications-page-title" data-testid="notifications-page-title">Notifications</h1>
            <p className="notifications-page-subtitle" data-testid="notifications-page-subtitle">
              Everything in one place. Opening this page marks your notifications as read.
            </p>
          </div>
          <Button onClick={onRefresh} variant="outline" disabled={isRefreshing} data-testid="notifications-page-refresh-button">
            <RefreshCcw className="h-4 w-4" /> {isRefreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </header>

        <div className="notif-tabs" role="tablist" aria-label="Notification filters" data-testid="notifications-page-tabs">
          {notificationTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              className={`notif-tab-btn ${activeTab === tab.id ? "active" : ""}`}
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`notifications-page-tab-${tab.id}`}
            >
              <span data-testid={`notifications-page-tab-label-${tab.id}`}>{tab.label}</span>
              <span className="notif-tab-count" data-testid={`notifications-page-tab-count-${tab.id}`}>{tabCounts[tab.id] || 0}</span>
            </button>
          ))}
        </div>

        <section className="notifications-card" data-testid="notifications-page-list-card">
          {isLoading ? (
            <p className="notifications-empty" data-testid="notifications-page-loading">Loading notifications…</p>
          ) : visibleItems.length === 0 ? (
            <p className="notifications-empty" data-testid="notifications-page-empty">No notifications in this category.</p>
          ) : (
            SECTION_ORDER.map((sectionKey) => {
              const sectionRows = groupedBySection[sectionKey] || [];
              if (!sectionRows.length) return null;
              return (
                <div className="notif-section" key={sectionKey} data-testid={`notifications-page-section-${sectionKey}`}>
                  <div className="notif-section-title" data-testid={`notifications-page-section-title-${sectionKey}`}>
                    {notificationSectionLabel[sectionKey]}
                  </div>
                  {sectionRows.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`notif-row ${item.status === "read" ? "read" : "unread"}`}
                      onClick={() => navigate(item.link || "/notifications")}
                      data-testid={`notifications-page-row-${item.id}`}
                    >
                      <div className="notif-row-left" data-testid={`notifications-page-row-kind-${item.id}`}>
                        <span className="notif-kind-pill">{item.kind_label}</span>
                        <Bell className="h-4 w-4" />
                      </div>
                      <div className="notif-row-body">
                        <p className="notif-row-title" data-testid={`notifications-page-row-title-${item.id}`}>{item.title}</p>
                        <p className="notif-row-subtitle" data-testid={`notifications-page-row-subtitle-${item.id}`}>{item.subtitle || ""}</p>
                      </div>
                      <div className="notif-row-right" data-testid={`notifications-page-row-time-${item.id}`}>
                        <span>{item.time_label}</span>
                        <ChevronRight className="h-4 w-4" />
                      </div>
                    </button>
                  ))}
                </div>
              );
            })
          )}
        </section>
      </div>
    </AppShell>
  );
}
